import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Typography, Card, message, Space } from 'antd';
import { ReloadOutlined, MailOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 0;
  }
  
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 0 !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const PendingStaff: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchPendingStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage]);

  const fetchPendingStaff = async () => {
    setLoading(true);
    try {
      const params: any = {
        pageId,
        perPage,
      };

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_PENDING_STAFF, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setStaff(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || paginator.totalCount || paginator.total || 0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: (data.itemsList || []).length,
          }));
        }
      } else {
        message.error('Failed to fetch pending staff');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch pending staff');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (record: any) => {
    try {
      await apiClient.post(API_ENDPOINTS.MERCHANT_RESEND_VERIFICATION_EMAIL, {
        email: record.email,
      });
      message.success('Verification email sent successfully');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to resend verification email');
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Country',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (code: string) => code ? (
        <Space>
          <ReactCountryFlag countryCode={code} svg style={{ width: '1.5em', height: '1.5em' }} />
          {code}
        </Space>
      ) : '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role ? <Tag color="cyan">{role}</Tag> : '-',
    },
    {
      title: 'Email Fail',
      dataIndex: 'emailFail',
      key: 'emailFail',
      render: (fail: boolean) => (
        <Tag color={fail ? 'red' : 'green'}>
          {fail ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: string) => {
        if (!date) return '-';
        const expiryDate = new Date(date);
        const now = new Date();
        const isExpired = expiryDate < now;
        return (
          <span style={{ color: isExpired ? '#EF4444' : COLORS.textPrimary }}>
            {expiryDate.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          icon={<MailOutlined />}
          onClick={() => handleResendEmail(record)}
          size="small"
        >
          Resend Email
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Pending Staff</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchPendingStaff}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderContainer>

      <StyledTableCard>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={staff}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} pending staff</span>,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </StyledTableCard>
    </PageContainer>
  );
};

export default PendingStaff;

